// @ts-nocheck
/**
 * PharosResponse — Primary assistant message renderer for Pharmax.ai
 * Renders reasoning, tool calls, chain-of-thought, widgets, suggestions, and more
 * Adapted from _zajaly/ARCHIVED-chat/pharos-response.tsx for the FastAPI+MongoDB backend
 */

import { memo, lazy, Suspense, useState, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { WidgetErrorBoundary } from '@/components/ui/widget-error-boundary'
import { LiteReasoning, LiteCodeBlock, LiteTool } from '@/components/chat/ai-lite'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { SuggestionChips } from '@/components/chat/suggestion-chips'
import { MessageFeedback } from '@/components/chat/message-feedback'
import { HumanReviewCard } from '@/components/chat/human-review-card'
import {
  Copy, Download, Maximize2, FileOutput,
  AlertTriangle, Lightbulb, Code2, BookOpen,
  BrainIcon, Beaker
} from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'

// ── Lazy-loaded widget renderers ──
const PlotWidget = lazy(() => import('@/components/widgets/plot-widget'))
const DataTableWidget = lazy(() => import('@/components/widgets/data-table-widget'))
const DataProfileWidget = lazy(() => import('@/components/widgets/data-profile-widget'))
const QCSummaryWidget = lazy(() => import('@/components/widgets/qc-summary-widget'))

const WidgetFallback = () => <Skeleton className="h-40 rounded-md" />

// ── Agent color map ──
const agentColors = {
  supervisor: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', dot: 'bg-indigo-500' },
  data_manager: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  pk_analyst: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', dot: 'bg-cyan-500' },
  popk_modeler: { bg: 'bg-blue-500/10', text: 'text-blue-400', dot: 'bg-blue-500' },
  clinical_pharm: { bg: 'bg-violet-500/10', text: 'text-violet-400', dot: 'bg-violet-500' },
  reg_intelligence: { bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-500' },
  qc_validator: { bg: 'bg-rose-500/10', text: 'text-rose-400', dot: 'bg-rose-500' },
  report_writer: { bg: 'bg-orange-500/10', text: 'text-orange-400', dot: 'bg-orange-500' },
  pharmagent: { bg: 'bg-primary/10', text: 'text-primary', dot: 'bg-primary' },
  system: { bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-muted-foreground' },
}

function getAgentStyle(agent) {
  if (!agent) return agentColors.pharmagent
  const key = agent.toLowerCase().replace(/[\s-]/g, '_')
  return agentColors[key] || agentColors.pharmagent
}

// ── Agent Badge ──
const AgentBadge = memo(function AgentBadge({ agent }) {
  const style = getAgentStyle(agent)
  const label = agent
    ? agent.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'PharmAgent'

  return (
    <div className="flex items-center gap-2 mb-2" data-testid="agent-badge">
      <Badge variant="outline" className={cn('gap-1.5 font-mono text-[10px] px-2 py-0.5 rounded-full', style.bg, style.text)}>
        <span className={cn('h-1.5 w-1.5 rounded-full', style.dot)} />
        {label}
      </Badge>
    </div>
  )
})

// ── Markdown Content ──
const MarkdownContent = memo(function MarkdownContent({ content }) {
  if (!content) return null
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none prose-p:text-sm prose-p:leading-relaxed prose-headings:text-sm prose-code:text-xs prose-pre:bg-muted/30 prose-pre:border prose-pre:border-border/30" data-testid="markdown-content">
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  )
})

// ── Code Block Widget ──
function CodeWidget({ widget }) {
  return (
    <div className="rounded-md border overflow-hidden" data-testid="code-widget">
      <LiteCodeBlock
        code={widget.content || ''}
        language={widget.metadata?.language || 'text'}
        filename={widget.title}
      />
    </div>
  )
}

// ── Recommendation Widget ──
function RecommendationWidget({ widget }) {
  return (
    <div className="flex gap-3 p-3 rounded-md border bg-accent/5" data-testid="recommendation-widget">
      <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Lightbulb className="w-4 h-4 text-accent" />
      </div>
      <div className="flex-1 min-w-0">
        <MarkdownContent content={widget.content} />
      </div>
    </div>
  )
}

// ── Error Widget ──
function ErrorWidget({ widget }) {
  return (
    <div className="flex items-start gap-2.5 p-3 rounded-md border border-destructive/30 bg-destructive/5" data-testid="error-widget">
      <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
      <span className="text-sm text-destructive/90">{widget.content}</span>
    </div>
  )
}

// ── Generic/Fallback Widget ──
function GenericWidget({ widget }) {
  if (widget.content) return <MarkdownContent content={widget.content} />
  if (widget.data) {
    return (
      <LiteCodeBlock code={JSON.stringify(widget.data, null, 2)} language="json" filename={widget.title} />
    )
  }
  return null
}

// ── Widget Router ──
function WidgetRenderer({ widget, index }) {
  return (
    <WidgetErrorBoundary>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.06 }}
        data-testid={`widget-${widget.type}-${index}`}
      >
        <Suspense fallback={<WidgetFallback />}>
          {(() => {
            switch (widget.type) {
              case 'plot': return <PlotWidget widget={widget} />
              case 'table':
              case 'parameter_table':
              case 'model_summary': return <DataTableWidget widget={widget} />
              case 'data_profile': return <DataProfileWidget widget={widget} />
              case 'qc_summary': return <QCSummaryWidget widget={widget} />
              case 'code': return <CodeWidget widget={widget} />
              case 'recommendation': return <RecommendationWidget widget={widget} />
              case 'error': return <ErrorWidget widget={widget} />
              case 'text': return null  // text widgets are rendered inline
              default: return <GenericWidget widget={widget} />
            }
          })()}
        </Suspense>
      </motion.div>
    </WidgetErrorBoundary>
  )
}

// ── Sources Block ──
function SourcesBlock({ sources }) {
  const [expanded, setExpanded] = useState(false)
  if (!sources?.length) return null

  return (
    <div className="mt-2" data-testid="sources-block">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <BookOpen className="w-3 h-3" />
        {sources.length} source{sources.length > 1 ? 's' : ''}
      </button>
      {expanded && (
        <div className="mt-2 space-y-1.5 pl-4 border-l-2 border-border/40">
          {sources.map((src, i) => (
            <div key={i} className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">[{i + 1}]</span>{' '}
              {src.title || src.url || src.content?.slice(0, 100)}
              {src.url && (
                <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                  link
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Plan Block ──
function PlanBlock({ plan }) {
  if (!plan) return null
  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-2" data-testid="plan-block">
      <div className="flex items-center gap-2 text-xs font-semibold">
        <Beaker className="w-3.5 h-3.5 text-primary" />
        <span style={{ fontFamily: 'Space Grotesk' }}>{plan.title || 'Analysis Plan'}</span>
      </div>
      {plan.steps?.map((step, i) => (
        <div key={i} className="flex items-start gap-2 text-xs">
          <span className={cn(
            'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5',
            step.status === 'complete' ? 'bg-green-500/15 text-green-500' :
            step.status === 'active' ? 'bg-primary/15 text-primary animate-pulse' :
            'bg-muted text-muted-foreground'
          )}>
            {step.status === 'complete' ? '✓' : i + 1}
          </span>
          <div className="min-w-0">
            <p className="font-medium">{step.name || step.description}</p>
            {step.agent && <p className="text-muted-foreground text-[10px] font-mono">{step.agent}</p>}
          </div>
        </div>
      ))}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════

export const PharosResponse = memo(function PharosResponse({
  message,
  isStreaming = false,
  isLast = false,
  onSuggestionClick,
  onAddToReport,
  sessionId,
}) {
  // ── Categorize widgets ──
  const textWidgets = useMemo(() => message.widgets?.filter(w => w.type === 'text') || [], [message.widgets])
  const nonTextWidgets = useMemo(() => message.widgets?.filter(w => w.type !== 'text' && w.type !== 'error') || [], [message.widgets])
  const errorWidgets = useMemo(() => message.widgets?.filter(w => w.type === 'error') || [], [message.widgets])

  // ── Derive text content ──
  const textContent = useMemo(() => {
    if (textWidgets.length) return textWidgets.map(w => w.content).join('\n\n')
    if (message.content) return message.content
    return ''
  }, [textWidgets, message.content])

  const hasVisibleContent = textContent || nonTextWidgets.length || errorWidgets.length ||
    message.reasoning || message.tool_calls?.length || message.plan ||
    message.chain_of_thought?.length || message.confirmation

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="group flex gap-3 px-3 sm:px-4 py-3"
      data-testid={`pharos-response-${message.id}`}
    >
      {/* Avatar column */}
      <div className="flex-shrink-0 mt-0.5">
        <div className={cn(
          'w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold',
          getAgentStyle(message.agent).bg, getAgentStyle(message.agent).text
        )}>
          P
        </div>
      </div>

      {/* Content column */}
      <div className="flex-1 min-w-0 space-y-3">
        {/* Agent badge */}
        <AgentBadge agent={message.agent} />

        {/* Reasoning / Thinking */}
        {message.reasoning && (
          <LiteReasoning isStreaming={isStreaming} defaultOpen={isStreaming}>
            {message.reasoning}
          </LiteReasoning>
        )}

        {/* Chain of Thought */}
        {message.chain_of_thought?.map((step, i) => (
          <LiteReasoning key={i} defaultOpen={false}>
            {typeof step === 'string' ? step : step.content || JSON.stringify(step)}
          </LiteReasoning>
        ))}

        {/* Tool Calls */}
        {message.tool_calls?.map((tool, i) => (
          <LiteTool
            key={tool.id || i}
            title={tool.name || tool.title || `Tool ${i + 1}`}
            type={tool.type || 'tool-call'}
            state={tool.status === 'error' ? 'output-error' : tool.status === 'running' ? 'input-available' : 'output-available'}
            input={tool.input || tool.arguments}
            output={tool.output || tool.result}
          />
        ))}

        {/* Plan */}
        {message.plan && <PlanBlock plan={message.plan} />}

        {/* Text content */}
        {textContent && <MarkdownContent content={textContent} />}

        {/* Error widgets */}
        {errorWidgets.map((w, i) => (
          <ErrorWidget key={w.id || `err-${i}`} widget={w} />
        ))}

        {/* Rich widgets (plots, tables, profiles, etc.) */}
        {nonTextWidgets.map((w, i) => (
          <WidgetRenderer key={w.id || `widget-${i}`} widget={w} index={i} />
        ))}

        {/* Confirmation / Human Review */}
        {message.confirmation && (
          <HumanReviewCard review={message.confirmation} />
        )}

        {/* Sources */}
        {message.sources?.length > 0 && <SourcesBlock sources={message.sources} />}

        {/* Streaming shimmer */}
        {isStreaming && !hasVisibleContent && (
          <div className="flex items-center gap-2">
            <Shimmer duration={1.5}>Analyzing...</Shimmer>
          </div>
        )}

        {/* Footer: Feedback + Suggestions */}
        {!isStreaming && (
          <div className="flex items-center gap-3 mt-1">
            <MessageFeedback messageId={message.id} sessionId={sessionId} agent={message.agent} />
          </div>
        )}

        {/* Suggestion chips — only on last message when not streaming */}
        {isLast && !isStreaming && message.suggested_actions?.length > 0 && (
          <SuggestionChips
            suggestions={message.suggested_actions}
            onClick={onSuggestionClick}
          />
        )}
      </div>
    </motion.div>
  )
})
